import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

// Utils
import { formatDate } from 'utils/formatString';

// Components
import SharingDiscountText from './components/SharingDiscountText';
import StatusPresent from './components/StatusPresent';

const SharingDiscount = ({
  unit,
  price,
  texts,
  isSeb,
  invites,
  familyDiscountParams,
  discountParams: {
    expirationDate,
    percent,
    priceWithoutDiscount,
  },
  discountParams,
}) => {
  const getText = useCallback((text, expDate = expirationDate) => {
    const dateFormat = formatDate(expDate);

    return text
      ?.replace?.(/{{DATE}}/g, dateFormat)
      ?.replace?.('{{PRICE}}', `${priceWithoutDiscount} ${unit}`);
  }, [formatDate, expirationDate]);

  const getPriceString = useCallback(() => {
    if (percent !== 100 && priceWithoutDiscount) {
      const fixedPrice = percent / 100 * priceWithoutDiscount;
      const truncPrice = Math.trunc(fixedPrice * 100) / 100;

      return `${truncPrice} ${unit}`;
    }
    return 'Бесплатно';
  }, [percent, priceWithoutDiscount]);

  const discountServiceConnected = (socName) => familyDiscountParams?.find?.((service) => service?.soc === socName);

  const setAdditionalDescription = () => {
    const { shareSebTextDiscount } = texts?.discountParams;

    if (familyDiscountParams) {
      const discountService = discountServiceConnected('IND_SALE');

      if (discountService?.soc === 'IND_SALE' && discountService?.expDate) {
        return getText(shareSebTextDiscount, discountService?.expDate);
      }
    }

    return null;
  };

  const setExpirationText = () => {
    const { expirationDateText } = texts?.discountParams;

    if (familyDiscountParams) {
      const discountService = discountServiceConnected('MNP_SEB');

      if (discountService?.soc === 'MNP_SEB' && discountService?.expDate) {
        return !!priceWithoutDiscount && getText(expirationDateText, discountService?.expDate);
      }

      return null;
    }

    return !!priceWithoutDiscount && getText(expirationDateText);
  };

  const renderTextBlock = () => {
    const {
      shareSebTitle,
      shareSdbTitle,
    } = texts?.discountParams;

    const priceWithoutDiscountWithUnit = unit ? `${priceWithoutDiscount} ${unit}` : priceWithoutDiscount;
    const priceWithUnit = () => {
      if (price === '0') {
        return 'Бесплатно';
      }
      return `${price} ${unit}`;
    };

    const discount = percent && getPriceString();

    const expText = setExpirationText();

    return (
      <SharingDiscountText
        label={isSeb ? shareSebTitle : shareSdbTitle}
        price={discountParams.priceWithoutDiscount ? priceWithoutDiscountWithUnit : priceWithUnit()}
        expirationText={expText}
        discount={discount}
        indSaleConnected={discountServiceConnected('IND_SALE')}
      />
    );
  };

  const renderStatusPresent = () => {
    const { shareSebTitle, shareSdbTitle, shareSebText, shareSdbText } = texts?.discountParams;

    const priceWithUnit = `${price} ${unit}`;

    const discount = percent && getPriceString();

    const additionalDescription = setAdditionalDescription();

    return (
      <StatusPresent
        price={priceWithUnit}
        discount={discount}
        statusText={{
          title: isSeb ? shareSebTitle : shareSdbTitle,
          description: isSeb ? shareSebText : shareSdbText,
          additionalDescription,
        }}
        emoji={discountServiceConnected('IND_SALE') && 'present'}
        indSaleConnected={discountServiceConnected('IND_SALE')}
      />
    );
  };

  const isStatusPresentMessage = discountParams && !invites?.length;

  return isStatusPresentMessage
    ? renderStatusPresent()
    : renderTextBlock();
};

SharingDiscount.propTypes = {
  familyDiscountParams: PropTypes.arrayOf(PropTypes.shape({
    expDate: PropTypes.string,
    soc: PropTypes.string,
    serviceConnected: PropTypes.bool,
  })),
  discountParams: PropTypes.shape({
    expirationDate: PropTypes.string,
    percent: PropTypes.number,
    priceWithoutDiscount: PropTypes.number,
  }),
  price: PropTypes.string,
  unit: PropTypes.string,
  invites: PropTypes.array,
  texts: PropTypes.object,
  isSeb: PropTypes.bool,
};

export default SharingDiscount;
